import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCaseStudy } from "@/lib/api";
import CTABlock from "@/components/shared/CTABlock";
import { ArrowLeft, ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import Magnetic from "@/components/shared/Magnetic";
import BillboardLogo from "@/components/shared/BillboardLogo";

const CONFIDENTIAL_SECTIONS = [
  "Full campaign brief and strategy",
  "Creator lineup and content mix",
  "Playlist and DSP support breakdown",
  "Chart velocity and streaming trajectory",
  "Spend, CPM, and outcome-tied reporting",
  "Post-campaign learnings and next steps",
];

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchCaseStudy(slug).then(setCs).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="pt-40 pb-20 text-center" data-testid="case-study-notfound">
        <h1 className="text-3xl font-display font-bold">Entry not found</h1>
        <Link to="/case-studies" className="mt-6 inline-block text-zinc-400 hover:text-white">← Back to the vault</Link>
      </div>
    );
  }
  if (!cs) return <div className="pt-40 h-screen" />;

  return (
    <div data-testid="page-case-study-detail">
      <section className="pt-24 md:pt-28 pb-8 md:pb-12">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <Link to="/case-studies" className="mb-8 inline-flex w-fit items-center gap-2 text-sm text-zinc-400 hover:text-white">
            <ArrowLeft size={16} /> All entries
          </Link>

          <div className="flex w-fit items-center gap-2 text-[11px] font-mono uppercase tracking-[0.24em] text-zinc-500 mb-6">
            <Lock size={12} /> Under NDA {cs.cover ? "· Featured Entry" : "· Redacted Entry"}
          </div>

          {cs.cover ? (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
              {/* Cover column */}
              <div className="md:col-span-5">
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <img
                    src={cs.cover}
                    alt={`${cs.artist} — ${cs.title || ""}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info column */}
              <div className="md:col-span-7">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
                  <span className="font-mono text-sm tracking-[0.28em] text-zinc-400">{cs.code}</span>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                    <ShieldCheck size={11} /> {cs.tier}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                    {cs.year} · {cs.region}
                  </span>
                  {cs.billboard && (
                    <span
                      data-testid="cs-billboard-badge"
                      className="inline-flex items-center gap-2 text-white/80 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.03]"
                      title="Billboard-charting campaign"
                    >
                      <BillboardLogo height={12} />
                      <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Charted</span>
                    </span>
                  )}
                </div>

                <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-balance leading-[0.98]">
                  {cs.artist}
                </h1>
                {cs.title && (
                  <div className="mt-3 italic font-light text-zinc-400 text-xl md:text-2xl">
                    "{cs.title}"
                  </div>
                )}
                <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                  {cs.genre}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {cs.outcomes.map((o) => (
                    <span
                      key={o}
                      data-testid={`cs-outcome-${o.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-300 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]"
                    >
                      {o}
                    </span>
                  ))}
                </div>

                {/* Spotify embed with fallback link */}
                {cs.spotify_track_id && (
                  <div className="mt-8" data-testid="cs-spotify-embed">
                    <iframe
                      title={`Spotify · ${cs.artist} · ${cs.title || ""}`}
                      src={`https://open.spotify.com/embed/track/${cs.spotify_track_id}?utm_source=generator&theme=0`}
                      width="100%"
                      height="152"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      style={{ borderRadius: "12px" }}
                    />
                    <a
                      href={`https://open.spotify.com/track/${cs.spotify_track_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors"
                    >
                      Open in Spotify <ArrowUpRight size={12} />
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-3">
                <span className="font-mono text-sm tracking-[0.28em] text-zinc-400">{cs.code}</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                  <ShieldCheck size={11} /> {cs.tier}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                  {cs.year} · {cs.region}
                </span>
                {cs.billboard && (
                  <span
                    data-testid="cs-billboard-badge"
                    className="inline-flex items-center gap-2 text-white/80 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.03]"
                    title="Billboard-charting campaign"
                  >
                    <BillboardLogo height={12} />
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Charted</span>
                  </span>
                )}
              </div>

              <h1 className="mt-6 font-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight text-balance">
                {cs.artist || cs.genre}
              </h1>
              {cs.title && (
                <div className="mt-3 italic font-light text-zinc-400 text-xl md:text-2xl">
                  "{cs.title}"
                </div>
              )}
              {cs.artist && (
                <div className="mt-4 text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                  {cs.genre}
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-2">
                {cs.outcomes.map((o) => (
                  <span
                    key={o}
                    data-testid={`cs-outcome-${o.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-300 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]"
                  >
                    {o}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="py-10 md:py-14 border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-7 md:p-10 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-[0.05]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 14px)",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.4) 0%, rgba(0,0,0,0) 55%)",
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-zinc-500 mb-5">
                  <Lock size={12} /> Under NDA
                </div>
                <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight text-balance">
                  Full case file
                  <span className="italic font-light"> available on request.</span>
                </h2>
                <p className="mt-5 max-w-md text-zinc-400 leading-relaxed">
                  We keep client rosters, budgets, and creator lineups confidential out of respect
                  for the artists, labels, and management teams we work with. On a strategy call
                  we walk qualified teams through the full playbook, live dashboards, and
                  outcome-tied numbers.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Magnetic radius={140} strength={0.4}>
                    <Link
                      to="/contact"
                      data-testid="cs-request-detail"
                      className="inline-flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-200 btn-magnetic glow-cta"
                    >
                      Request access <ArrowUpRight size={18} />
                    </Link>
                  </Magnetic>
                  <Link
                    to="/case-studies"
                    className="inline-flex items-center gap-2 border border-white/20 text-white rounded-full px-6 py-3.5 hover:bg-white/5 transition-colors"
                  >
                    Browse the vault
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-5">Inside the full report</div>
                <ul className="space-y-3">
                  {CONFIDENTIAL_SECTIONS.map((s) => (
                    <li
                      key={s}
                      data-testid={`cs-confidential-${s.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                      className="flex items-start gap-3 border-b border-white/5 last:border-0 pb-3 last:pb-0"
                    >
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/60 flex-none" />
                      <span className="text-zinc-300 leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABlock />
    </div>
  );
}
